import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Link,
  Tooltip,
  Stack,
  Typography,
  useTheme,
  Box,
  useMediaQuery,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchGet } from '../../api/common';
import { track } from '../../api/track';
import { ScoreCardModal } from '../../modals/ScoreCardModal';
import { Player, Mode, DetailedPlayer, DbQuestionData } from '../room/types';
import { assignRanks, utcToLocalDateTime } from '../room/utils';
import { BiGhost } from 'react-icons/bi';
import { Game, Teams } from '@par/shared/types/game';
import { Profile } from '@par/shared/types/player';

const MAX_PLAYERS_IN_TEAMS = 8;
let joinTeamButtonTimeout;

type PropTypes = {
  currentWeek: number;
  handleJoinGameClick: (gameId: string, mode: Mode) => void;
  handleLeaveGameClick: (gameId: string, profileId: string) => void;
  handlePickTeamsClick: (gameId: string) => void;
  handleRemoveGameClick: (gameId: string) => void;
  handleSpectateClick: (gameId: string) => void;
  lobbyGames?: Game[];
  profile: Profile;
};

export const LeagueLobby = ({
  currentWeek,
  handleJoinGameClick,
  handleLeaveGameClick,
  handlePickTeamsClick,
  handleRemoveGameClick,
  handleSpectateClick,
  lobbyGames,
  profile,
}: PropTypes) => {
  const navigate = useNavigate();
  const [openScorecardModal, setOpenScorecardModal] = useState(false);
  const [gameResults, setGameResults] = useState<
    Game & { questionSet: DbQuestionData[] }
  >();
  const [noGamesToDisplay, setNoGamesToDisplay] = useState(false);
  const [disabledTeamJoinButton, setDisabledTeamJoinButton] = useState(false);

  const theme = useTheme();
  const xsBreakpoint = useMediaQuery(theme.breakpoints.only('xs'));

  const joinTeamGameClick = (gameId: string, mode: Mode) => {
    clearTimeout(joinTeamButtonTimeout);
    setDisabledTeamJoinButton(true);
    joinTeamButtonTimeout = setTimeout(() => {
      setDisabledTeamJoinButton(false);
    }, 2000);
    handleJoinGameClick(gameId, mode);
  };

  const actionButton = (r: Game) => {
    if (r.gameOver) return null;
    if (
      // (profile.admin || r.whoCanJoin?.includes(profile.id)) &&
      r.gameStarted &&
      !r.gameOver &&
      !r.activePlayers.find((profileId: string) => profileId === profile.id)
    ) {
      return (
        <Button
          variant="contained"
          sx={{
            backgroundColor: 'secondary.main',
            color: 'secondary.contrastText',
          }}
          onClick={() => handleSpectateClick(r.gameId)}
        >
          Spectate
        </Button>
      );
    } else if (
      !profile.admin &&
      !r.whoCanJoin?.includes(profile.id) &&
      !r.gameStarted &&
      r.confirmingPlayers
    ) {
      return (
        <Typography variant="subtitle2">
          League games cannot be spectated
        </Typography>
      );
    }
    if (
      r.mode === 'teams' &&
      !r.gameStarted &&
      !r.confirmingPlayers &&
      canJoin(r, profile.id, profile.admin) &&
      r.activePlayers.length < MAX_PLAYERS_IN_TEAMS
    ) {
      if (!r.activePlayers?.includes(profile.id)) {
        return (
          <Stack direction={'row'} spacing={1}>
            <Button
              variant="contained"
              sx={{ width: 80 }}
              onClick={() => joinTeamGameClick(r.gameId, r.mode)}
              disabled={
                r.activePlayers.length >= MAX_PLAYERS_IN_TEAMS ||
                disabledTeamJoinButton
              }
            >
              {disabledTeamJoinButton ? 'Joining' : 'Join'}
            </Button>
            {profile.admin ? (
              <Button
                variant="contained"
                sx={{ width: 80 }}
                onClick={() => handleRemoveGameClick(r.gameId)}
              >
                Remove
              </Button>
            ) : null}
          </Stack>
        );
      } else {
        return (
          <Stack direction={'row'} spacing={1}>
            <Button
              variant="contained"
              sx={{ width: 80 }}
              onClick={() => handlePickTeamsClick(r.gameId)}
              disabled={r.activePlayers.length >= MAX_PLAYERS_IN_TEAMS}
            >
              Pick Teams
            </Button>
          </Stack>
        );
      }
    }
    return (
      <Typography variant="subtitle2">
        League games cannot be spectated
      </Typography>
    );
  };

  const canJoin = (game: Game, profileId: string, isAdmin: boolean) => {
    if (isAdmin || !game.whoCanJoin || game.whoCanJoin.length === 0)
      return true;
    if (game.whoCanJoin?.length > 0 && game.whoCanJoin?.includes(profileId))
      return true;
    return false;
  };

  const handleUsernameClick = async (e: any, player: Player) => {
    e.stopPropagation();
    await track(profile.id, 'lobby.username.link', player.username);
    navigate(`/profile/overview/${player.profileId}`);
  };

  const getRatingNextToUsername = (
    mode: Mode,
    gameOver: boolean,
    player: Player
  ) => {
    const formatRating = (rating: number) => {
      const rounded = Math.floor(rating);
      return `${rounded > 0 ? '+' : ''}${rounded}`;
    };

    if (gameOver) {
      return formatRating(player.rankedRatingDiff || 0);
    }
    return (player.rankedMatchGamesPlayed || 0) +
      (player.rankedSitAndGoGamesPlayed || 0) <
      10
      ? 'calibrating'
      : player.rankedRating;
  };

  const ratingColor = (gameOver: boolean, player: Player) => {
    if (gameOver) {
      if (Math.round(player.rankedRatingDiff || 0) > 0)
        return theme.palette.success.main;
      if (Math.round(player.rankedRatingDiff || 0) < 0)
        return theme.palette.error.main;
      return theme.palette.warning.main;
    }
    return theme.palette.ranked.main;
  };

  const getJoinedPlayers = (game: Game) => {
    if (!game.players || Object.keys(game.players).length === 0) return '--';
    const rankedPlayers = assignRanks(
      Object.values(game.players).sort((a, b) => a.parScore - b.parScore)
    );
    return (
      <Stack width={'100%'}>
        {rankedPlayers.slice(0, 20).map((p, i) => (
          <Link
            key={i}
            component="button"
            variant="body2"
            onClick={(e) => handleUsernameClick(e, p)}
            textAlign={'left'}
          >
            <Stack direction={'row'} sx={{ whiteSpace: 'nowrap' }}>
              {game.mode === 'sit-n-go'
                ? `${game.gameOver ? p.rank : i + 1}.`
                : ''}{' '}
              {p.username}
              {(game.mode === 'match' || game.mode === 'sit-n-go') &&
              game.ranked ? (
                <Stack direction={'row'}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: theme.palette.ranked.main }}
                  >
                    &nbsp;(
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: ratingColor(game.gameOver, p) }}
                  >
                    {getRatingNextToUsername(game.mode, game.gameOver, p)}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: theme.palette.ranked.main }}
                  >
                    )
                  </Typography>
                </Stack>
              ) : null}
            </Stack>
          </Link>
        ))}
      </Stack>
    );
  };

  const isJoined = (game: Game) => {
    if (!game.activePlayers) return false;
    return (
      Object.keys(game.activePlayers)?.includes(profile.id) && !game.gameOver
    );
  };

  const handleViewResultsClick = async (game: Game) => {
    const res = await fetchGet(`/games/result/${game.gameId}`);
    res.game.questionSet = res.questionSet;
    setGameResults(res.game);
    setOpenScorecardModal(true);
  };

  const results = (game: Game) => (
    <Stack spacing={1}>
      <Typography variant="body2">
        {utcToLocalDateTime(game.createdAt)}
      </Typography>
      {/* {currentWeek && game.leagueWeek < currentWeek ? ( */}
      <Link
        sx={{ cursor: 'pointer' }}
        onClick={() => handleViewResultsClick(game)}
      >
        View Results
      </Link>
      {/* ) : profile.admin || playerCanJoin(profile.id, game.teams) ? (
        <Link
          sx={{ cursor: 'pointer' }}
          onClick={() => handleViewResultsClick(game)}
        >
          View Results (teams only)
        </Link>
      ) : (
        <Typography variant="caption">
          Results available for team members only
        </Typography>
      )} */}
    </Stack>
  );

  const playerCanJoin = (profileId: string, teams: Teams) => {
    if (
      teams.teamA.members.includes(profileId) ||
      teams.teamB.members.includes(profileId)
    )
      return true;
    return false;
  };

  const stackName = (r: Game) => (
    <Tooltip placement="top" title={`Game ID: ${r.gameId}`}>
      <Stack>
        <Stack direction="row" spacing={1}>
          {r.ranked ? (
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                color: theme.palette.ranked.main,
              }}
            >
              Ranked
            </Typography>
          ) : null}
          {r.ghostChallenge ? (
            <BiGhost
              style={{
                fontSize: '18px',
                color: theme.palette.warning.main,
              }}
            />
          ) : null}
        </Stack>
        {r.customGameName || 'N/A'}
      </Stack>
    </Tooltip>
  );

  const getStatus = (r: Game) => {
    if (
      r.mode === 'sit-n-go' &&
      !r.gameStarted &&
      r.activePlayers &&
      !r.confirmingPlayers
    ) {
      return `${r.activePlayers.length} / ${r.sitAndGoMaxPlayers}`;
    }
    if (!r.gameStarted && r.confirmingPlayers) {
      return 'Starting';
    }
    if (r.gameStarted && !r.gameOver) {
      return 'In progress';
    }
    return r.status;
  };

  const lobbyTable = (
    <TableContainer component={Paper}>
      <Table aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell width={'20%'}>Name</TableCell>
            <TableCell width={'5%'} sx={{ textAlign: 'center' }}>
              Match
            </TableCell>
            <TableCell width={'5%'} sx={{ textAlign: 'center' }}>
              Game
            </TableCell>
            <TableCell width={'30%'}>Players</TableCell>
            <TableCell width={'15%'}>Status</TableCell>
            <TableCell width={'20%'}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!noGamesToDisplay ? (
            lobbyGames
              ?.sort((a, b) => b.createdAt - a.createdAt)
              .map((r: Game) => (
                <Box key={r.gameId} width="100%" display="contents">
                  <TableRow
                    sx={{
                      border: isJoined(r) ? '2px solid' : 'none',
                      borderColor: isJoined(r) ? 'secondary.main' : 'none',
                    }}
                  >
                    <TableCell>{stackName(r)}</TableCell>
                    <TableCell>
                      <Typography textAlign={'center'}>
                        {r.leagueWeek}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography textAlign={'center'}>
                        {r.leagueGame}
                      </Typography>
                    </TableCell>
                    <TableCell>{getJoinedPlayers(r)}</TableCell>
                    <TableCell>{getStatus(r)}</TableCell>
                    <TableCell>
                      {r.status === 'Finished' ? results(r) : actionButton(r)}
                    </TableCell>
                  </TableRow>
                </Box>
              ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} style={{ textAlign: 'center' }}>
                No games to display
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      {gameResults && profile.id ? (
        <ScoreCardModal
          bannedCategories={gameResults.bannedCategories}
          categorySet={gameResults.categorySet}
          gameType={gameResults.gameType}
          gameId={gameResults.gameId}
          handicap={gameResults.handicap}
          onClose={() => setOpenScorecardModal(false)}
          openScordcardModal={openScorecardModal}
          league={gameResults.league}
          mode={gameResults.mode}
          players={gameResults.players as Record<string, DetailedPlayer>}
          profileId={
            profile
              ? profile.id
              : Object.values(gameResults.players)[0].profileId
          }
          questionSet={gameResults.questionSet}
          ranked={gameResults.ranked}
          seededBy={gameResults.matchMakingSeededBy}
          survivalMode={gameResults.survivalMode}
          teams={gameResults.teams}
          variant={gameResults.variant}
        />
      ) : null}
      {lobbyTable}
    </>
  );
};
